import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { designTokens } from "@/shared/styles/tokens";

import "./route-progress.css";

const MIN_VISIBLE_MS = 400;
const FINISH_ANIMATION_MS = 250;
const FADE_ANIMATION_MS = 180;
const MAX_PENDING_MS = 15000;

const RouteReadyContext = createContext<(key: string) => void>(() => {});

const ProgressBar = ({ ready }: { ready: boolean }) => {
  const [progress, setProgress] = useState(10);
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (ready || !visible) return;

    const interval = window.setInterval(() => {
      setProgress((current) => Math.min(90, current + Math.max(1, Math.ceil((90 - current) * 0.16))));
    }, 350);

    return () => window.clearInterval(interval);
  }, [ready, visible]);

  useEffect(() => {
    if (!ready) return;

    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - startedAt.current));
    const finish = window.setTimeout(() => setProgress(100), remaining);
    const fade = window.setTimeout(() => setFading(true), remaining + FINISH_ANIMATION_MS);
    const hide = window.setTimeout(
      () => setVisible(false),
      remaining + FINISH_ANIMATION_MS + FADE_ANIMATION_MS,
    );

    return () => {
      window.clearTimeout(finish);
      window.clearTimeout(fade);
      window.clearTimeout(hide);
    };
  }, [ready]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setVisible(false), MAX_PENDING_MS);
    return () => window.clearTimeout(timeout);
  }, []);

  if (!visible) return null;

  return (
    <div
      aria-label="Loading page"
      className="route-progress"
      role="progressbar"
      style={{
        backgroundColor: designTokens.color.action.primary,
        opacity: fading ? 0 : 1,
        width: `${progress}%`,
      }}
    />
  );
};

export const RouteProgressProvider = ({ children }: React.PropsWithChildren) => {
  const { key } = useLocation();
  const [readyKey, setReadyKey] = useState<string | null>(null);
  const markReady = useCallback((routeKey: string) => setReadyKey(routeKey), []);

  return (
    <RouteReadyContext.Provider value={markReady}>
      <ProgressBar key={key} ready={readyKey === key} />
      {children}
    </RouteReadyContext.Provider>
  );
};

export const RouteReadyMarker = () => {
  const { key } = useLocation();
  const markReady = useContext(RouteReadyContext);

  useEffect(() => {
    markReady(key);
  }, [key, markReady]);

  return null;
};
